import React, { useEffect, useMemo, useState } from 'react';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { usePersistedDataStore } from '../../store.js';
import {
  countCasesWithPartyEvidence,
  EVIDENCE_PARTIES,
} from './nyscefExhibitUtils.js';
import { downloadBulkEvidence } from './downloadEvidenceService.js';

const colors = {
  bgPrimary: '#ffffff',
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
  townAccent: '#B85454',
  townBg: '#FDF6F6',
};

const SectionLabel = ({ children }) => (
  <Typography
    component="p"
    sx={{
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: colors.textTertiary,
      m: '0 0 8px',
    }}
  >
    {children}
  </Typography>
);

const PartyCard = ({ active, partyKey, name, description, onClick }) => {
  const isTown = partyKey === EVIDENCE_PARTIES.TOWN;
  const accent = isTown ? colors.townAccent : colors.accent;
  const activeBg = isTown ? colors.townBg : colors.accentBg;

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        border: '1.5px solid',
        borderColor: active ? accent : colors.borderTertiary,
        borderRadius: '8px',
        p: '12px 14px',
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
        background: active ? activeBg : colors.bgPrimary,
        '&:hover': { borderColor: active ? accent : colors.borderSecondary },
      }}
    >
      <Box sx={{ fontSize: 20, color: active ? accent : colors.textSecondary, mb: 0.75 }}>
        <PictureAsPdfOutlinedIcon fontSize="inherit" />
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>
        {name}
      </Typography>
      <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
        {description}
      </Typography>
    </Box>
  );
};

const BulkDownloadEvidenceDialog = ({ open, onClose, selectedRows, rows }) => {
  const negotiationObj = usePersistedDataStore((s) => s.negotiationObj);
  const [party, setParty] = useState(EVIDENCE_PARTIES.AVENTINE);
  const [mergePdfs, setMergePdfs] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { casesInScope, townCaseCount, aventineCaseCount } = useMemo(() => {
    const rowsToUse = selectedRows?.length ? selectedRows : rows;
    const parcelIds = new Set(rowsToUse?.map((r) => r.parcel_id).filter(Boolean) ?? []);
    const cases = (negotiationObj?.cases ?? []).filter((c) => parcelIds.has(c.parcel_id));
    return {
      casesInScope: cases,
      townCaseCount: countCasesWithPartyEvidence(cases, EVIDENCE_PARTIES.TOWN),
      aventineCaseCount: countCasesWithPartyEvidence(cases, EVIDENCE_PARTIES.AVENTINE),
    };
  }, [selectedRows, rows, negotiationObj?.cases]);

  useEffect(() => {
    if (!open) return;
    setParty(EVIDENCE_PARTIES.AVENTINE);
    setMergePdfs(false);
    setDownloading(false);
  }, [open]);

  const selectedCaseCount =
    party === EVIDENCE_PARTIES.TOWN ? townCaseCount : aventineCaseCount;
  const canDownload = selectedCaseCount > 0 && !downloading;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const result = await downloadBulkEvidence({
        negotiationObj,
        selectedRows,
        rows,
        party,
        mergePdfs,
        onLoadingChange: setDownloading,
      });
      if (result?.success) onClose();
    } finally {
      setDownloading(false);
    }
  };

  const scopeLabel = selectedRows?.length
    ? `${selectedRows.length} selected case${selectedRows.length === 1 ? '' : 's'}`
    : `${casesInScope.length} case${casesInScope.length === 1 ? '' : 's'}`;

  return (
    <StableMuiDialog
      open={open}
      onClose={downloading ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 460,
          borderRadius: '16px',
          border: `0.5px solid ${colors.borderSecondary}`,
          bgcolor: colors.bgPrimary,
          m: 2,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          component="h2"
          sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}
        >
          Download evidence
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: '0 0 20px' }}>
          Download NYSCEF exhibit PDFs for {scopeLabel}
        </Typography>

        <SectionLabel>Evidence source</SectionLabel>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            mb: 2,
          }}
        >
          <PartyCard
            active={party === EVIDENCE_PARTIES.AVENTINE}
            partyKey={EVIDENCE_PARTIES.AVENTINE}
            name="Our evidence"
            description={
              aventineCaseCount > 0
                ? `${aventineCaseCount} case${aventineCaseCount === 1 ? '' : 's'} with Aventine exhibits`
                : 'No Aventine exhibits found'
            }
            onClick={() => setParty(EVIDENCE_PARTIES.AVENTINE)}
          />
          <PartyCard
            active={party === EVIDENCE_PARTIES.TOWN}
            partyKey={EVIDENCE_PARTIES.TOWN}
            name="Town evidence"
            description={
              townCaseCount > 0
                ? `${townCaseCount} case${townCaseCount === 1 ? '' : 's'} with town exhibits`
                : 'No town exhibits found'
            }
            onClick={() => setParty(EVIDENCE_PARTIES.TOWN)}
          />
        </Box>

        <SectionLabel>Download as</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
          <PartyCard
            active={mergePdfs}
            partyKey="combined"
            name="Combined file"
            description="One PDF with every exhibit"
            onClick={() => !downloading && setMergePdfs(true)}
          />
          <PartyCard
            active={!mergePdfs}
            partyKey="individual"
            name="Individual files"
            description="A zip of each exhibit PDF"
            onClick={() => !downloading && setMergePdfs(false)}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            pt: 0.5,
          }}
        >
          <Button
            onClick={onClose}
            disabled={downloading}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: colors.textSecondary,
              border: `0.5px solid ${colors.borderSecondary}`,
              borderRadius: '8px',
              px: 2,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            disableElevation
            disabled={!canDownload}
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              bgcolor: colors.accent,
              borderRadius: '8px',
              px: 2.5,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
              '&:hover': { bgcolor: '#144f8a' },
            }}
          >
            {downloading ? 'Downloading…' : 'Download'}
          </Button>
        </Box>
      </Box>
    </StableMuiDialog>
  );
};

export default BulkDownloadEvidenceDialog;
